/* eslint-disable max-lines */
/* eslint camelcase: ["error", {allow: ["^UNSAFE_"]}] */
import React, {Component, type Node} from 'react';
import classNames from 'classnames';

import type {VoidFunctionType} from 'constants/flow/flowTypes';

import s from './input.scss';

let lastId = 0;

type PropsType = {
    value?: string,
    name?: string,
    type?: string,
    id?: string,
    maxLength?: string | null,
    label?: string,
    onChange: VoidFunctionType<{event: SyntheticInputEvent, dom: () => {}}>,
    handleBlur?: VoidFunctionType<{event: SyntheticInputEvent, name: string}>,
    validation: (value: string) => string,
    error?: string,
    activeClass?: string,
    placeholder?: string,
    withOutIcons?: boolean,
    icon?: Node,
    design?: 'primary' | 'header',
    setRef?: VoidFunctionType<{}>,
    required: boolean,
    onInvalid?: (event?: FormEvent<HTMLInputElement>) => void,
};

type StateType = {
    isActive: boolean,
    error: string,
    id: number,
};

/**
 * Input component
 */
export default class Input extends Component<PropsType, StateType> {
    static defaultProps = {
        value: '',
        name: '',
        type: 'text',
        id: '',
        label: '',
        maxLength: null,
        withOutIcons: false,
        activeClass: '',
        placeholder: '',
        error: false,
        icon: null,
        design: 'primary',
        handleBlur: (name: string, value: string) => {}, // eslint-disable-line no-unused-vars
        setRef: () => {},
        required: false,
        onInvalid: () => {},
    };

    /**
     * @param {PropsType} props Props passed during object creation
     */
    constructor(props: PropsType) {
        super(props);
        this.state = {
            isActive: false,
            error: this.props.error,
            id: lastId += 1,
        };
    }

    /**
     * Called each time component receives new props
     * @param {{value: string}} nextProps New props
     */
    UNSAFE_componentWillReceiveProps(nextProps: {value: string}) {
        if (nextProps.value !== this.props.value) {
            if (nextProps.value === '') this.changeInputActivity(false);
            this.setState({error: ''});
        }
    }

    /**
     * Blur handler
     * @param {SyntheticInputEvent} event Received event
     */
    handleBlur = (event: SyntheticInputEvent) => {
        const {value, name} = event.target;

        if (value === '') this.changeInputActivity(false);

        if (typeof this.props.validation === 'function') {
            this.setState({error: this.props.validation(value)});
        }

        if (typeof this.props.validation === 'string') {
            this.props.handleBlur(value, name, this.props.validation);
        }

        this.props.handleBlur(event, this.node);
    };

    /**
     * Change input state
     * @param {boolean} activity Input state
     */
    changeInputActivity = (activity: boolean) => {
        this.setState({isActive: activity});
    };

    /**
     * Change the input state value
     * @param {SyntheticInputEvent} event Received event
     */
    handleChange = (event: SyntheticInputEvent) => {
        if (typeof this.props.onChange !== 'undefined') {
            this.props.onChange(event, this.node);
        }
    };

    /**
     * Calculate class for input
     * @param {string} error - start
     * @returns {string} - className class name
     */
    prepareInputStation = (error: string): string => {
        let className = '';

        if (error === 'success' || this.props.validation === false) {
            className = s.inputSuccess;
        } else if (error.length > 0 && error !== 'success') {
            className = s.inputError;
        }

        return className;
    };

    /**
     * Change ref and move it to parent
     * @param {Node} ref - input ref
     */
    updateRef = (ref: Node) => {
        this.node = ref;
        this.props.setRef(ref);
    };

    /**
     * Ref for input element
     */
    node: () => {};

    /**
     * Render jsx to html
     * @returns {Node} Rendered react component
     */
    render(): Node {
        const {
            value,
            name,
            type,
            maxLength,
            label,
            withOutIcons,
            activeClass,
            placeholder,
            design,
            icon,
            required,
            onInvalid,
        } = this.props;
        const {isActive} = this.state;
        let {error} = this.state;

        if (this.props.error && this.props.error !== this.state.error) {
            // eslint-disable-next-line prefer-destructuring
            error = this.props.error;
        }

        const className = this.prepareInputStation(error);
        const activeCls = activeClass || s.inputActive;

        return (
            <div
                className={classNames(
                    s.input,
                    className,
                    s[design],
                    {[s.withOutIcons]: withOutIcons},
                    {[activeCls]: value !== '' || isActive === true},
                )}
            >
                <div className={s.wrapInput}>
                    <input
                        ref={this.updateRef}
                        id={`${this.props.id}-${this.state.id}`}
                        type={type}
                        name={name}
                        value={value}
                        maxLength={maxLength}
                        className={s.inputField}
                        onFocus={
                            /* istanbul ignore next */ () => {
                                this.changeInputActivity(true);
                            }
                        }
                        onBlur={this.handleBlur}
                        onChange={this.handleChange}
                        placeholder={placeholder}
                        required={required}
                        onInvalid={onInvalid}
                    />
                </div>
                <label htmlFor={`${this.props.id}-${this.state.id}`} className={s.inputLabel}>
                    {label}
                </label>
                {icon && <div className={s.icon}>{icon}</div>}
                <span className={s.errorMessage}>{error}</span>
            </div>
        );
    }
}
